import React, { Fragment, useEffect, useMemo, useRef } from 'react';
import { styled } from 'dripsy';
import { View } from 'react-native';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { useMaskedInputProps } from 'react-native-mask-input';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import isValid from 'date-fns/isValid';

import { TextInput, TextInputRefType } from '../../common/TextInput';
import { Calendar } from '../Calendar';
import { useMeasureLayout } from '@hooks';
import { MeasureDimensionsType } from '@types';
import { IS_WEB } from '@utils';
import { INPUT_DATE_MASK } from '@constants';
import type { IInputDatePickerProps } from './InputDatePicker';

const CalendarContainer = styled(View)(
  (props: Pick<MeasureDimensionsType, 'top'>) => ({
    pt: '$7',
    px: '$6',
    pb: '$6',
    backgroundColor: '$background0',
    variant: 'shadows.popover',
    width: 284,
    minHeight: 288,
    borderRadius: 8,
    position: 'absolute',
    zIndex: 1,
    right: 0,
    top: props.top + 51 + 3,
  }),
);

type StoreType = {
  inputValue: string;
  dimensions: MeasureDimensionsType | null;
  isShowCalendar: boolean;
  setDimensions(dimensions: MeasureDimensionsType): void;
  setInputValue(value: string): void;
  toggleCalendar(): void;
};

/**
 * This is Web only component
 * For mobile we using separate component based on react-native-modal-datetime-picker
 */
export const InputDatePicker = observer<IInputDatePickerProps>(
  ({ containerRef, onChangeValue, ...props }) => {
    const inputRef = useRef<TextInputRefType>(null);
    const measureLayout = useMeasureLayout(inputRef, containerRef);
    const store = useLocalObservable<StoreType>(() => ({
      inputValue: '',
      dimensions: null,
      isShowCalendar: false,

      setInputValue(value) {
        this.inputValue = value;
      },

      setDimensions(dimensions) {
        this.dimensions = dimensions;
      },

      toggleCalendar() {
        this.isShowCalendar = !this.isShowCalendar;
      },
    }));

    useEffect(() => {
      if (IS_WEB) {
        // Listen for clicks outside the calendar
        document.addEventListener('click', () => {
          if (store.isShowCalendar) {
            store.toggleCalendar();
          }
        });
      }
    }, []);

    function onPressInputIcon() {
      if (!store.isShowCalendar) {
        measureLayout((d) => store.setDimensions(d));
      }
      store.toggleCalendar();
    }

    function onDayPress(date: Date) {
      const dateString = format(date, 'dd/MM/yyyy');

      store.toggleCalendar();
      store.setInputValue(dateString);

      if (onChangeValue) {
        onChangeValue(date);
      }
    }

    function onChangeText(dateString: string) {
      const date = parse(dateString, 'dd/MM/yyyy', new Date());

      store.setInputValue(dateString);

      if (onChangeValue) {
        onChangeValue(date);
      }
    }

    const maskedProps = useMaskedInputProps({
      value: store.inputValue,
      mask: INPUT_DATE_MASK,
      onChangeText,
    });

    // Pares string value into Date type for calendar
    const selectedDate = useMemo(() => {
      const date = parse(maskedProps.value, 'dd/MM/yyyy', new Date());

      return isValid(date) ? date : undefined;
    }, [maskedProps.value]);

    const initialDate = useMemo(
      () => (selectedDate ? selectedDate : props.value),
      [selectedDate, props.value],
    );

    return (
      <Fragment>
        <TextInput
          {...props}
          value={maskedProps.value}
          onChangeText={maskedProps.onChangeText}
          inputRef={inputRef}
          iconRight={{
            name: 'calendar-light',
            onPress: onPressInputIcon,
          }}
        />
        {store.dimensions && store.isShowCalendar && (
          <CalendarContainer top={store.dimensions.top}>
            <Calendar
              initialDate={initialDate}
              onDayPress={onDayPress}
              selectedDate={selectedDate}
            />
          </CalendarContainer>
        )}
      </Fragment>
    );
  },
);
